import SelectPickerInput from 'components/forms/inputs/rhf-inputs/SelectPickerInput';
import { useDateRangeModal } from 'components/modals/date-range/useDateRangeModal';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { createContext, useContext } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { DashboardForm, TimeRangeOption, timeRangeOptions } from 'types/dashboard';
import { t } from 'utils/getTranslations';
import { getDisplayLabel } from './util';

/**
 * Set by a card that shows the picker once in its own header (Home's My time): the pickers its tab panels carry
 * render nothing inside it.
 */
export const PeriodPickerInHeader = createContext(false);

/** In a card header, a 30px trigger with the field's value in 13px medium type, as Home draws it. */
const HEADER_CLASS =
    '[&_[data-slot=select-trigger]]:border-border-control [&_[data-slot=select-trigger]]:h-[30px]! [&_[data-slot=select-trigger]]:text-meta [&_[data-slot=select-trigger]]:font-medium';

/** The dashboard's shared period selector, bound to the page's `DashboardForm`. */
const PeriodPicker = ({ inHeader = false }: { inHeader?: boolean }) => {
    const hoisted = useContext(PeriodPickerInHeader);
    return hoisted && !inHeader ? null : <PeriodPickerField inHeader={inHeader} />;
};

const PeriodPickerField = ({ inHeader }: { inHeader: boolean }) => {
    const { control, setValue } = useFormContext<DashboardForm>();
    const effectiveFormat = useEffectiveDateFormat();
    const [dateFrom, dateTo, selectedTimeRange] = useWatch({
        control,
        name: ['dateFrom', 'dateTo', 'selectedTimeRange'],
    });

    const { handleSelectChange } = useDateRangeModal<TimeRangeOption>({
        onDateRangeSelected: (option, newDateFrom, newDateTo) => {
            setValue('dateFrom', newDateFrom);
            setValue('dateTo', newDateTo);
            setValue('selectedTimeRange', option);
        },
        initialDateFrom: dateFrom,
        initialDateTo: dateTo,
    });

    return (
        <>
            {/* The select shows only its value, so this names it; the trigger's id is the field name. */}
            <label htmlFor="selectedTimeRange" className="sr-only">
                {t('dashboard.period')}
            </label>
            <SelectPickerInput
                control={control}
                name="selectedTimeRange"
                options={timeRangeOptions}
                getOptionIdentifier={(a) => a.value}
                getOptionLabel={(a) => a.label}
                getSelectedValueLabel={(option) =>
                    getDisplayLabel(option, selectedTimeRange, dateFrom, dateTo, effectiveFormat)
                }
                onChange={handleSelectChange}
                alwaysTriggerOnChangeForValues={['custom']}
                wrapperClassName={inHeader ? HEADER_CLASS : undefined}
            />
        </>
    );
};

export default PeriodPicker;
